import { closestPointOnSegment, distance } from '../map/document.ts';
import type { Checkpoint } from '../map/types.ts';
import type { Vec2 } from '../types.ts';
import type { Raster } from './raster.ts';

/**
 * How fast open water lets a hull go, in px/turn. `turnPx` is the hull's across-the-bow reach;
 * `vMaxPx` the ring's top speed (Infinity when the ring has none); `vFloorPx` what a boat can
 * always creep at, however tight the water.
 */
export type SpeedLimits = {
  turnPx: number;
  vMaxPx: number;
  vFloorPx: number;
};

/**
 * Coarse grid the per-gate fields run on: raster cells merged `factor` × `factor`. A merged cell
 * is water if any of its raster cells is, so a narrow channel stays open; `vcap` is the speed the
 * widest of them allows.
 */
export type FieldGrid = {
  cell: number;
  cols: number;
  rows: number;
  solid: Uint8Array;
  vcap: Float64Array;
};

/**
 * Turns to go (`time`) and px to go (`dist`, along that same quickest route) from every cell to
 * one gate. Infinity where the gate cannot be reached.
 */
export type GateField = {
  time: Float64Array;
  dist: Float64Array;
};

export const FIELD_FACTOR = 2;

/**
 * Speed a hull can carry with `clearance` px of water around it. At speed v a turn of reach T
 * bends with radius about v² / 2T, so the radius that still fits the water caps v at √(2·T·c).
 */
export function speedCap(clearance: number, limits: SpeedLimits): number {
  return Math.min(limits.vMaxPx, Math.sqrt(2 * limits.turnPx * clearance) + limits.vFloorPx);
}

export function buildFieldGrid(
  raster: Raster,
  limits: SpeedLimits,
  factor: number = FIELD_FACTOR,
): FieldGrid {
  const cols = Math.ceil(raster.cols / factor);
  const rows = Math.ceil(raster.rows / factor);
  const solid = new Uint8Array(cols * rows);
  const vcap = new Float64Array(cols * rows);

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      let widest = -1;

      for (let dy = 0; dy < factor; dy += 1) {
        for (let dx = 0; dx < factor; dx += 1) {
          const rr = row * factor + dy;
          const rc = col * factor + dx;

          if (rr < raster.rows && rc < raster.cols && raster.solid[rr * raster.cols + rc] === 0) {
            widest = Math.max(widest, raster.clearance[rr * raster.cols + rc]!);
          }
        }
      }

      const index = row * cols + col;

      if (widest < 0) {
        solid[index] = 1;
      } else {
        vcap[index] = speedCap(widest, limits);
      }
    }
  }

  return { cell: raster.cell * factor, cols, rows, solid, vcap };
}

/** Binary min-heap of cell indices keyed by time, with lazy deletion. */
class CellHeap {
  private cells: number[] = [];
  private keys: number[] = [];

  get size(): number {
    return this.cells.length;
  }

  push(cell: number, key: number): void {
    const cells = this.cells;
    const keys = this.keys;
    let index = cells.length;
    cells.push(cell);
    keys.push(key);

    while (index > 0) {
      const parent = (index - 1) >> 1;

      if (keys[parent]! <= key) {
        break;
      }

      cells[index] = cells[parent]!;
      keys[index] = keys[parent]!;
      index = parent;
    }

    cells[index] = cell;
    keys[index] = key;
  }

  /** Pops the smallest entry into `out`. */
  pop(out: { cell: number; key: number }): void {
    const cells = this.cells;
    const keys = this.keys;
    out.cell = cells[0]!;
    out.key = keys[0]!;
    const lastCell = cells.pop()!;
    const lastKey = keys.pop()!;
    const size = cells.length;

    if (size === 0) {
      return;
    }

    let index = 0;

    for (;;) {
      const left = index * 2 + 1;

      if (left >= size) {
        break;
      }

      const right = left + 1;
      const child = right < size && keys[right]! < keys[left]! ? right : left;

      if (keys[child]! >= lastKey) {
        break;
      }

      cells[index] = cells[child]!;
      keys[index] = keys[child]!;
      index = child;
    }

    cells[index] = lastCell;
    keys[index] = lastKey;
  }
}

const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

/** Quickest-route Dijkstra from every water cell the gate segment touches. */
export function buildGateField(grid: FieldGrid, gate: Checkpoint): GateField {
  const { cell, cols, rows, solid, vcap } = grid;
  const time = new Float64Array(cols * rows).fill(Infinity);
  const dist = new Float64Array(cols * rows).fill(Infinity);
  const heap = new CellHeap();
  const span = distance(gate.a, gate.b);
  const samples = Math.max(1, Math.ceil(span / (cell / 2)));

  for (let s = 0; s <= samples; s += 1) {
    const t = s / samples;
    const col = Math.floor((gate.a.x + (gate.b.x - gate.a.x) * t) / cell);
    const row = Math.floor((gate.a.y + (gate.b.y - gate.a.y) * t) / cell);

    if (col < 0 || row < 0 || col >= cols || row >= rows) {
      continue;
    }

    const index = row * cols + col;

    if (solid[index]) {
      continue;
    }

    const centre = { x: (col + 0.5) * cell, y: (row + 0.5) * cell };
    const gap = distance(centre, closestPointOnSegment(centre, gate.a, gate.b));
    const seconds = gap / vcap[index]!;

    if (seconds < time[index]!) {
      time[index] = seconds;
      dist[index] = gap;
      heap.push(index, seconds);
    }
  }

  const top = { cell: 0, key: 0 };

  while (heap.size > 0) {
    heap.pop(top);
    const index = top.cell;

    if (top.key > time[index]!) {
      continue;
    }

    const col = index % cols;
    const row = (index - col) / cols;
    const here = 1 / vcap[index]!;

    for (const [dx, dy, step] of NEIGHBOURS) {
      const nc = col + dx;
      const nr = row + dy;

      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) {
        continue;
      }

      const next = nr * cols + nc;

      if (solid[next]) {
        continue;
      }

      // No squeezing diagonally between two land cells.
      if (dx !== 0 && dy !== 0 && (solid[row * cols + nc] || solid[nr * cols + col])) {
        continue;
      }

      const length = step * cell;
      const candidate = time[index]! + length * 0.5 * (here + 1 / vcap[next]!);

      if (candidate < time[next]!) {
        time[next] = candidate;
        dist[next] = dist[index]! + length;
        heap.push(next, candidate);
      }
    }
  }

  return { time, dist };
}

export type FieldSample = { time: number; dist: number };

/** Bilinear sample over the reachable corners. Null when none of the four is reachable. */
export function sampleGateField(
  grid: FieldGrid,
  field: GateField,
  point: Vec2,
): FieldSample | null {
  const gx = point.x / grid.cell - 0.5;
  const gy = point.y / grid.cell - 0.5;
  const c0 = Math.floor(gx);
  const r0 = Math.floor(gy);
  const fx = gx - c0;
  const fy = gy - r0;
  let weight = 0;
  let time = 0;
  let dist = 0;

  for (let dy = 0; dy <= 1; dy += 1) {
    for (let dx = 0; dx <= 1; dx += 1) {
      const col = c0 + dx;
      const row = r0 + dy;

      if (col < 0 || row < 0 || col >= grid.cols || row >= grid.rows) {
        continue;
      }

      const index = row * grid.cols + col;
      const t = field.time[index]!;

      if (!Number.isFinite(t)) {
        continue;
      }

      const w = (dx ? fx : 1 - fx) * (dy ? fy : 1 - fy) + 1e-9;
      weight += w;
      time += w * t;
      dist += w * field.dist[index]!;
    }
  }

  return weight > 0 ? { time: time / weight, dist: dist / weight } : null;
}
